import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import Modal from '../../components/common/Modal';
import UsuarioForm from './UsuarioForm';
import useSortable from '../../hooks/useSortable';
import usePaginacao from '../../hooks/usePaginacao';
import { useAuth } from '../../contexts/AuthContext';
import * as usuariosApi from '../../api/usuarios';
import { labelPerfil } from '../../utils/webhookOpcoes';

export default function Usuarios() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState(null);
  const [erro, setErro] = useState('');
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [resetando, setResetando] = useState(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [erroReset, setErroReset] = useState('');
  const [okReset, setOkReset] = useState('');

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await usuariosApi.listar();
      setUsuarios(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os usuários.'));
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const { sorted, Th } = useSortable(usuarios || [], 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted, 15);

  if (erro && !usuarios) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!usuarios) return <LoadingSpinner />;

  const salvar = async (dados) => {
    if (editando) {
      await usuariosApi.atualizar(editando.id_usuario_portal, dados);
    } else {
      await usuariosApi.criar(dados);
    }
    await carregar();
  };

  const confirmarReset = async () => {
    setErroReset('');
    setOkReset('');
    try {
      await usuariosApi.resetarSenha(resetando.id_usuario_portal, novaSenha);
      setOkReset('Senha redefinida. Informe a nova senha ao usuário.');
      setNovaSenha('');
    } catch (err) {
      setErroReset(extrairMensagemErro(err, 'Não foi possível redefinir a senha.'));
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Usuários"
        subtitulo={`${usuarios.length} usuário(s) com acesso ao portal`}
        acoes={
          <button
            type="button"
            className="wh-btn wh-btn-primary"
            onClick={() => {
              setEditando(null);
              setFormAberto(true);
            }}
          >
            <i className="bi bi-person-plus" /> Novo usuário
          </button>
        }
      />

      <div className="card-soft">
        <div className="wh-table-wrap">
          <table className="wh-table">
            <thead>
              <tr>
                <Th campo="nome">Nome</Th>
                <Th campo="email">E-mail</Th>
                <Th campo="perfil">Perfil</Th>
                <Th campo="ativo">Situação</Th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paginados.map((u) => (
                <tr key={u.id_usuario_portal}>
                  <td style={{ fontWeight: 600 }}>{u.nome}</td>
                  <td>{u.email}</td>
                  <td>
                    <Pill tone={u.perfil === 'consulta' ? 'neutral' : 'dark'}>{labelPerfil(u.perfil)}</Pill>
                  </td>
                  <td>
                    <StatusBadge status={u.ativo ? 'ativo' : 'inativo'} />
                  </td>
                  <td>
                    <div className="d-flex gap-1 justify-content-end">
                      <button
                        type="button"
                        className="wh-btn wh-btn-outline wh-btn-sm"
                        title="Editar"
                        onClick={() => {
                          setEditando(u);
                          setFormAberto(true);
                        }}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        type="button"
                        className="wh-btn wh-btn-outline wh-btn-sm"
                        title="Redefinir senha"
                        onClick={() => {
                          setResetando(u);
                          setNovaSenha('');
                          setErroReset('');
                          setOkReset('');
                        }}
                      >
                        <i className="bi bi-key" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Paginacao />

      {formAberto && (
        <UsuarioForm
          usuario={editando}
          perfilAtual={user?.perfil}
          onClose={() => setFormAberto(false)}
          onSalvar={salvar}
        />
      )}

      {resetando && (
        <Modal titulo={`Redefinir senha de ${resetando.nome}`} onClose={() => setResetando(null)} largura={440}>
          <label className="wh-label">Nova senha</label>
          <input
            type="password"
            className="wh-input"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            minLength={8}
          />
          <div className="wh-hint">
            Mínimo de 8 caracteres. Trocar a senha derruba as sessões abertas desse usuário.
          </div>
          {erroReset && (
            <div className="alert alert-danger py-2 px-3 mt-3" style={{ fontSize: 13 }}>
              {erroReset}
            </div>
          )}
          {okReset && (
            <div className="alert alert-success py-2 px-3 mt-3" style={{ fontSize: 13 }}>
              {okReset}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="wh-btn wh-btn-outline" onClick={() => setResetando(null)}>
              Fechar
            </button>
            <button
              type="button"
              className="wh-btn wh-btn-primary"
              onClick={confirmarReset}
              disabled={novaSenha.length < 8}
            >
              Redefinir
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
